import styled from 'styled-components';
import {
    error,
    maximumYellow100,
    maximumYellow25,
    maximumYellow700,
    robotoRegular,
    scarpaGray100,
    scarpaGray25,
    scarpaGray300,
    scarpaGray400,
    scarpaGray50,
    scarpaGray500,
    scarpaGray800,
    scarpaGray900,
    spanishViridian25,
    spanishViridian50,
    spanishViridian500,
    spanishViridian600,
    tomatoCoral100,
    tomatoCoral25,
    tomatoCoral600,
    white,
} from '@habitech/shared';
import { PanelPaymentStatus } from 'src/redux/panel_transactions/panel_transactions.types';

const searchPillFocusBorder = ($invalid?: boolean, $disabled?: boolean) => {
    if ($disabled) return scarpaGray100;
    if ($invalid) return error;
    return spanishViridian500;
};

export const TransactionsRoot = styled.div`
    font-family: ${robotoRegular};
    margin-top: 20px;
`;

/**
 * Contenedor único de la pestaña. Sin `overflow: hidden` a propósito: recortaría
 * la lista de los filtros cuando la tarjeta es corta (pocas filas).
 */
export const Card = styled.div`
    background: ${white};
    border: 1px solid ${scarpaGray100};
    border-radius: 12px;
`;

/**
 * Misma plantilla de columnas que la tabla (`buildTransactionsColumnLayout`):
 * el conteo cierra la columna de producto y la cifra de la suma abre la de
 * monto. El "Suma:" se pega a esa cifra (4px), sin desplazarla.
 */
export const CardHeader = styled.div<{ $template: string; $stacked?: boolean }>`
    display: grid;
    grid-template-columns: ${({ $template }) => $template};
    align-items: ${({ $stacked }) => ($stacked ? 'start' : 'center')};
    padding: 16px 0;

    .heading {
        grid-column: tx / amount;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
        min-width: 0;
        padding: 0 16px;
    }

    .heading-icon {
        display: inline-flex;
        color: ${spanishViridian500};
    }

    .heading-title {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
    }

    h3 {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
        color: ${scarpaGray900};
    }

    /* Con un cliente el conteo nombra el correo y no cabe en la línea del
       título. Baja debajo, alineado con "Órdenes y transacciones", no con el
       icono ni empujado a la derecha por el margen del escenario de cartera. */
    .heading-stacked {
        display: grid;
        grid-template-columns: auto 1fr;
        grid-template-areas:
            'icon title'
            '. metrics';
        align-items: center;
        column-gap: 10px;
        row-gap: 2px;
    }

    .heading-stacked .heading-icon {
        grid-area: icon;
    }

    .heading-stacked .heading-title {
        grid-area: title;
    }

    .heading-stacked .heading-metrics {
        grid-area: metrics;
        margin: 0;
        white-space: normal;
    }

    .heading-metrics {
        display: flex;
        align-items: baseline;
        gap: 8px;
        margin: 0 0 0 auto;
        font-size: 13px;
        color: ${scarpaGray500};
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }

    .heading-metrics-with-sum {
        margin-right: 40px;
    }

    .heading-dot {
        color: ${scarpaGray300};
    }

    .heading-amount-slot {
        grid-column: amount;
        position: relative;
        min-width: 0;
        padding: 0 16px;
        font-size: 13px;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }

    .heading-amount-label {
        position: absolute;
        left: 16px;
        transform: translateX(calc(-100% - 4px));
        font-weight: 400;
        color: ${scarpaGray500};
    }

    .heading-amount {
        font-weight: 600;
        color: ${scarpaGray800};
    }

    .actions {
        grid-column: pago / -1;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
        padding: 0 16px;
    }
`;

export const NewBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    background: ${spanishViridian500};
    color: ${white};
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
`;

export const FiltersRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 0 20px 16px;
`;

/**
 * Control con forma de pastilla. `position: relative` ancla la lista de opciones
 * al control, que es lo que un `select` nativo no permite hacer: el navegador
 * decide dónde abrir su lista y la termina superponiendo sobre el filtro.
 */
export const FilterPill = styled.div<{ $open?: boolean }>`
    position: relative;

    /* Toda la pastilla es el disparador, no solo el texto del valor: la flecha,
       el icono y el espacio interior también abren la lista. */
    .pill-trigger {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 7px 12px;
        border: 1px solid ${({ $open }) => ($open ? spanishViridian500 : scarpaGray100)};
        border-radius: 999px;
        background: ${white};
        font-family: ${robotoRegular};
        font-size: 13px;
        color: ${scarpaGray800};
        cursor: pointer;
        outline: none;

        &:hover,
        &:focus-visible {
            border-color: ${spanishViridian500};
        }

        /* Apagado en el modo búsqueda por número, donde el filtro no se aplica.
           Se deja visible en lugar de esconderlo: un control que desaparece se
           lee como una pantalla rota, uno gris se lee como una regla. */
        &:disabled {
            cursor: not-allowed;
            background: ${scarpaGray25};
            color: ${scarpaGray300};

            .pill-value,
            .pill-icon,
            .pill-caret {
                color: ${scarpaGray300};
            }

            &:hover {
                border-color: ${scarpaGray100};
            }
        }
    }

    .pill-icon {
        display: inline-flex;
        color: ${scarpaGray400};
    }

    .pill-value {
        font-weight: 500;
        color: ${scarpaGray900};
    }

    .pill-caret {
        display: inline-flex;
        color: ${scarpaGray400};
        transition: transform 0.15s ease;
        transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
    }
`;

/**
 * Buscador por número de transacción. Tiene forma de pastilla porque comparte
 * fila con los filtros, pero no es un filtro: el número desplaza a los demás
 * controles en lugar de sumarse (ver `Transactions.tsx`).
 */
export const SearchPill = styled.form<{
    $invalid?: boolean;
    $wide?: boolean;
    $disabled?: boolean;
}>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 6px 5px 12px;
    border: 1px solid ${({ $invalid }) => ($invalid ? error : scarpaGray100)};
    border-radius: 999px;
    background: ${({ $disabled }) => ($disabled ? scarpaGray25 : white)};

    &:focus-within {
        border-color: ${({ $invalid, $disabled }) =>
            searchPillFocusBorder($invalid, $disabled)};
    }

    .search-icon {
        display: inline-flex;
        color: ${scarpaGray400};
    }

    input {
        width: ${({ $wide }) => ($wide ? '200px' : '96px')};
        border: none;
        outline: none;
        background: transparent;
        font-family: ${robotoRegular};
        font-size: 13px;
        color: ${({ $disabled }) => ($disabled ? scarpaGray300 : scarpaGray900)};

        &::placeholder {
            color: ${scarpaGray400};
        }

        &:disabled {
            cursor: not-allowed;
        }
    }

    .search-action {
        display: inline-flex;
        align-items: center;
        padding: 4px;
        border: none;
        border-radius: 50%;
        background: transparent;
        color: ${scarpaGray500};
        cursor: pointer;

        &:hover {
            color: ${scarpaGray900};
        }

        &:disabled {
            cursor: not-allowed;
            color: ${scarpaGray300};
        }
    }
`;

export const SearchError = styled.span`
    font-size: 12px;
    color: ${error};
`;

/**
 * Aviso del modo búsqueda: dice qué se está mostrando y que los filtros no
 * aplican, y es el único lugar desde donde se sale del modo.
 *
 * Va en ámbar porque es exactamente lo que el color sabe hacer: hay uno solo en
 * pantalla, aparece solo en este modo y lo que anuncia es una salvedad —los
 * controles de la fila de arriba están apagados—.
 */
export const LookupNotice = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 20px 12px;
    padding: 8px 12px;
    border: 1px solid ${maximumYellow100};
    border-radius: 8px;
    background: ${maximumYellow25};
    font-size: 12px;
    color: ${scarpaGray800};

    .notice-icon {
        display: inline-flex;
        color: ${maximumYellow700};
    }

    .notice-spacer {
        flex: 1 1 auto;
    }
`;

/** Lista de opciones: siempre debajo de la pastilla, nunca encima. */
export const OptionList = styled.div`
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    min-width: 100%;
    max-height: 240px;
    overflow-y: auto;
    padding: 4px;
    background: ${white};
    border: 1px solid ${scarpaGray100};
    border-radius: 10px;
    box-shadow: 0 8px 20px rgba(29, 26, 32, 0.12);
`;

export const OptionItem = styled.button<{ $active?: boolean; $selected?: boolean }>`
    width: 100%;
    text-align: left;
    border: none;
    border-radius: 6px;
    padding: 8px 10px;
    background: ${({ $active }) => ($active ? spanishViridian25 : 'transparent')};
    font-family: ${robotoRegular};
    font-size: 13px;
    white-space: nowrap;
    cursor: pointer;
    color: ${({ $selected }) => ($selected ? spanishViridian600 : scarpaGray800)};
    font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
`;

/** Recordatorio de que el incremento no permite modificar operaciones. */
export const ReadOnlyTag = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    border: 1px solid ${scarpaGray100};
    border-radius: 999px;
    background: ${scarpaGray25};
    font-size: 12px;
    color: ${scarpaGray500};
    white-space: nowrap;
`;

export const ScopeSelector = styled.div`
    display: inline-flex;
    align-items: center;
    border: 1px solid ${scarpaGray100};
    border-radius: 999px;
    overflow: hidden;
    background: ${white};
`;

export const ScopeOption = styled.button<{ $active?: boolean }>`
    border: none;
    background: ${({ $active }) => ($active ? spanishViridian25 : white)};
    color: ${({ $active }) => ($active ? spanishViridian600 : scarpaGray500)};
    font-family: ${robotoRegular};
    font-size: 13px;
    font-weight: ${({ $active }) => ($active ? 600 : 400)};
    padding: 7px 14px;
    cursor: pointer;

    & + & {
        border-left: 1px solid ${scarpaGray100};
    }

    &:disabled {
        cursor: not-allowed;
        color: ${scarpaGray300};
        background: ${scarpaGray25};
    }
`;

export const LoadingBar = styled.div`
    position: relative;
    height: 2px;
    width: 100%;
    background: ${spanishViridian25};
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: 40%;
        background: ${spanishViridian500};
        animation: transactions-loading-slide 1.1s infinite ease-in-out;
    }

    @keyframes transactions-loading-slide {
        0% {
            left: -40%;
        }
        100% {
            left: 100%;
        }
    }
`;

export const TableArea = styled.div<{ $loading?: boolean }>`
    transition: opacity 0.2s ease;
    opacity: ${({ $loading }) => ($loading ? 0.5 : 1)};
    pointer-events: ${({ $loading }) => ($loading ? 'none' : 'auto')};
`;

export const TableWrapper = styled.div`
    width: 100%;
    overflow-x: auto;
    border-top: 1px solid ${scarpaGray50};
`;

export const Table = styled.table`
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    font-size: 13px;
    color: ${scarpaGray900};

    thead th {
        text-align: left;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.6px;
        font-size: 10px;
        color: ${scarpaGray400};
        background: ${white};
        padding: 12px 16px;
        white-space: nowrap;
        border-bottom: 1px solid ${scarpaGray50};
    }

    tbody td {
        padding: 12px 16px;
        border-bottom: 1px solid ${scarpaGray50};
        vertical-align: middle;
        white-space: nowrap;
        overflow: hidden;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }

    /* Celda de dos líneas: el dato que importa arriba, el contexto abajo. */
    .cell-main {
        display: block;
        font-weight: 600;
        color: ${scarpaGray900};
    }

    .cell-sub {
        display: block;
        margin-top: 2px;
        font-size: 12px;
        font-weight: 400;
        color: ${scarpaGray500};
    }

    .amount-cell .cell-main {
        font-variant-numeric: tabular-nums;
    }

    /* Nombre, correo, origen o método largos: se recortan en la celda y el
       completo queda en el tooltip. Sin esto se pintan encima de la columna
       de al lado. */
    .customer-cell .cell-main,
    .customer-cell .cell-sub,
    [data-id='transactions-origin'] .cell-main,
    [data-id='transactions-origin'] .cell-sub,
    .amount-cell .cell-main,
    .amount-cell .cell-sub {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    /* La etiqueta de producto es el único texto largo: se deja crecer en dos
       líneas en lugar de ensanchar toda la tabla. */
    .product-cell {
        white-space: normal;
        min-width: 200px;
        max-width: 300px;
    }

    /* Pago es la única columna con datos reales en este incremento: se resalta
       para que se lea primero. */
    .payment-cell {
        background: ${scarpaGray25};
    }

    thead th.payment-cell {
        background: ${scarpaGray25};
        color: ${spanishViridian600};
    }

    .pending-cell {
        color: ${scarpaGray300};
    }
`;

const STATUS_COLORS: Record<
    PanelPaymentStatus,
    { text: string; background: string; border: string }
> = {
    successful: {
        text: spanishViridian600,
        background: spanishViridian25,
        border: spanishViridian50,
    },
    in_process: {
        text: maximumYellow700,
        background: maximumYellow25,
        border: maximumYellow100,
    },
    failed: { text: tomatoCoral600, background: tomatoCoral25, border: tomatoCoral100 },
};

export const StatusBadge = styled.span<{ $status: PanelPaymentStatus | null }>`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    color: ${({ $status }) => ($status ? STATUS_COLORS[$status].text : scarpaGray500)};
    background: ${({ $status }) =>
        $status ? STATUS_COLORS[$status].background : scarpaGray25};
    border: 1px solid
        ${({ $status }) => ($status ? STATUS_COLORS[$status].border : scarpaGray100)};
`;

export const EmptyState = styled.div`
    padding: 32px 20px;
    text-align: center;
    color: ${scarpaGray500};
    font-size: 14px;
    border-top: 1px solid ${scarpaGray50};
`;

export const ErrorState = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin: 0 20px 16px;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1px solid ${tomatoCoral100};
    color: ${error};
    background: ${tomatoCoral25};
    font-size: 13px;
`;

export const CardFooter = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 20px;
    border-top: 1px solid ${scarpaGray50};
`;

/**
 * Contenedor del paginador compartido. `PagerBar` trae un `margin-top` propio
 * pensado para su uso original en Asignaciones; aquí ya vive dentro del pie de
 * la tarjeta y sobra.
 */
export const FooterPager = styled.div`
    > div {
        margin-top: 0;
    }
`;

/** Aviso del corte del histórico: no se muestran datos dudosos sin advertirlo. */
export const HistoryNote = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${scarpaGray500};
`;
